import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, PrimaryButton, SingleSelect, Text, Textfield } from "widgets";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

import {
  VALID_VERTICAL_IDS,
  BUSINESS_VERTICAL_IDS,
} from "constants/constants";
import Loader from "shared/Loader/Loader";
import { useFetchCustomerInfo } from "store/backend/apiHooks";
import MultipleUsers from "components/MultipleUsers/MultipleUsers";

import styles from "./HomePage.module.scss";

const handlePhoneNumberChange = (event, setPhoneNumber) => {
  const rawValue = event.target.value || "";
  const digitsOnly = rawValue.replace(/\D/g, "");
  setPhoneNumber(digitsOnly.slice(0, 10));
};

const isSelfLookup = (customerPhone, cashierPhone) => {
  if (!customerPhone || !cashierPhone) {
    return false;
  }

  return customerPhone.replace(/\D/g, "") === cashierPhone.replace(/\D/g, "");
};

const handleClickViewCustomerOrders = (
  phoneNumber,
  navigate,
  openSnackbar,
  selectedBusinessVertical,
  fetchCustomerInfo,
  setShowMultipleUsersDialog,
  setMultipleUsersList
) => {
  fetchCustomerInfo(
    { phoneNumber, selectedBusinessVertical },
    {
      onSuccess: (customerDetails) => {
        if (!Array.isArray(customerDetails)) {
          openSnackbar("Unexpected response format.", "error");
          return;
        }
        if (customerDetails.length > 1) {
          setShowMultipleUsersDialog(true);
          setMultipleUsersList(customerDetails);
          return;
        }
        if (customerDetails.length === 1) {
          const customerId = customerDetails[0].uamId;
          navigate(`/customerOrders?customerId=${customerId}`);
          return;
        }
        openSnackbar(
          "No customer found with the provided phone number.",
          "error"
        );
      },
      onError: (error) => {
        openSnackbar(
          error.message || "Failed to fetch customer info.",
          "error"
        );
      },
    }
  );
};

const handleChangeBusinessVertical = (event, setSelectedBusinessVertical) => {
  const selectedBusinessVerticalId = event.target.value;
  setSelectedBusinessVertical(selectedBusinessVerticalId);
};

const isValidPhoneNumber = (phoneNumber) => {
  const digits = phoneNumber.replace(/\D/g, "");
  return digits.length === 10;
};

const isViewCustomerOrdersButtonDisabled = (
  phoneNumber,
  selectedBusinessVertical,
  cashierPhoneNumber,
  openSnackbar
) => {
  if (isSelfLookup(phoneNumber, cashierPhoneNumber)) {
    openSnackbar("You cannot search for your own details.", "error");
    return true;
  }
  return (
    isValidPhoneNumber(phoneNumber) &&
    VALID_VERTICAL_IDS.includes(selectedBusinessVertical)
  );
};

const HomePage = () => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbarContext();
  const [cashierPhoneNumber] = useState(
    localStorage.getItem("cashierPhoneNumber")
  );
  const [showMultipleUsersDialog, setShowMultipleUsersDialog] = useState(false);
  const [multipleUsersList, setMultipleUsersList] = useState([]);

  const [selectedBusinessVertical, setSelectedBusinessVertical] = useState();
  const [phoneNumber, setPhoneNumber] = useState("");

  const { mutate: fetchCustomerInfo, isPending: isCustomerInfoLoading } =
    useFetchCustomerInfo();

  if (isCustomerInfoLoading) {
    return <Loader />;
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.searchCustomerContainer}>
        <div className={styles.inputContainer}>
          <Text>Please enter customer's registered mobile number.</Text>
          <Textfield
            value={phoneNumber}
            handleOnChange={(event) =>
              handlePhoneNumberChange(event, setPhoneNumber)
            }
            label="Enter a Phone number"
          />
        </div>
        <div className={styles.businessVertical}>
          <Text>Please select the business vertical</Text>
          <SingleSelect
            tag="Select business vertical"
            value={selectedBusinessVertical}
            menuItems={BUSINESS_VERTICAL_IDS}
            handleChange={(event) =>
              handleChangeBusinessVertical(event, setSelectedBusinessVertical)
            }
          />
        </div>
        <div className={styles.viewCustomerDetailsButton}>
          <PrimaryButton
            onClick={() =>
              handleClickViewCustomerOrders(
                phoneNumber,
                navigate,
                openSnackbar,
                selectedBusinessVertical,
                fetchCustomerInfo,
                setShowMultipleUsersDialog,
                setMultipleUsersList
              )
            }
            disabled={isViewCustomerOrdersButtonDisabled(
              phoneNumber,
              selectedBusinessVertical,
              cashierPhoneNumber,
              openSnackbar
            )}
          >
            {isCustomerInfoLoading ? <Loader /> : "View Customer Details"}
          </PrimaryButton>
        </div>
      </div>
      <Dialog
        open={showMultipleUsersDialog}
        handleClose={() => setShowMultipleUsersDialog(false)}
      >
        <MultipleUsers
          customerDetails={multipleUsersList}
          handleOnSelectUser={(uamId) => {
            setShowMultipleUsersDialog(false);
            navigate(`/customerOrders?customerId=${uamId}`);
          }}
        />
      </Dialog>
    </div>
  );
};

export default HomePage;
